<template>
  <div class="pa-0 secondary--text text--darken-3">
    <!-- Header Summary -->
    <div class="bg-grey-light pa-8 border-bottom">
      <div class="caption primary--text font-weight-black text-uppercase tracking-widest mb-4 d-flex align-center">
        <v-icon x-small color="primary" class="mr-2">fas fa-info-circle</v-icon> Identificación de Producto
      </div>
      
      <v-row dense>
        <v-col cols="12" md="4">
          <div class="caption grey--text font-weight-bold uppercase mb-1">Referencia / SKU</div>
          <div class="body-1 font-weight-black">{{ sku }}</div>
        </v-col>
        <v-col cols="12" md="4">
          <div class="caption grey--text font-weight-bold uppercase mb-1">Orden de Trabajo</div>
          <div class="body-1 font-weight-black primary--text">#{{ workOrderId }}</div>
        </v-col>
        <v-col cols="12" md="4">
          <div class="caption grey--text font-weight-bold uppercase mb-1">Nombre / Etiqueta</div>
          <div class="body-1 font-weight-bold text-truncate">{{ product.name }}</div>
        </v-col>
      </v-row>
    </div>

    <!-- Traceability Timeline -->
    <div class="pa-8 bg-white">
      <div class="caption primary--text font-weight-black text-uppercase tracking-widest mb-6 d-flex align-center">
        <v-icon x-small color="primary" class="mr-2">fas fa-history</v-icon> Línea de Tiempo del Flujo
      </div>

      <div class="modern-timeline pl-4 pt-2">
        <div 
          v-for="(log, index) in product.pre_stage_logs.slice().reverse()" 
          :key="log.id" 
          class="timeline-item pb-8 d-flex align-start"
          :class="{ 'last-item': index === product.pre_stage_logs.length - 1 }"
        >
          <div class="timeline-dot-container d-flex flex-column align-center mr-6">
            <v-avatar 
              :color="getStageColor(log.stage)" 
              size="32" 
              class="shadow-sm border-white"
            >
              <v-icon x-small color="white">{{ getStageIcon(log.stage) }}</v-icon>
            </v-avatar>
            <div class="timeline-line flex-grow-1 mt-2" v-if="index !== product.pre_stage_logs.length - 1"></div>
          </div>
          
          <div class="timeline-content flex-grow-1">
            <div class="d-flex align-center justify-space-between mb-1">
              <span class="body-1 font-weight-black" :style="{ color: getStageHex(log.stage) }">
                {{ getStageName(log.stage) }}
              </span>
              <span class="caption grey--text font-weight-bold">{{ log.date | moment("DD/MM/YYYY HH:mm") }}</span>
            </div>
            <div class="d-flex align-center">
              <v-avatar size="20" color="grey lighten-4" class="mr-2">
                <v-icon x-small color="grey darken-1">fas fa-user</v-icon>
              </v-avatar>
              <span class="caption grey--text text--darken-1 font-weight-bold">{{ log.email }}</span>
            </div>
          </div>
        </div>
        
        <!-- Empty State -->
        <div v-if="product.pre_stage_logs.length === 0" class="text-center py-8 opacity-40">
          <v-icon large class="mb-3">fas fa-folder-open</v-icon>
          <div class="body-2 font-weight-bold">No hay registros de trazabilidad todavía</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DetailPrestages',
  props: ['product'],
  data() {
    return {
      // stages removido de data para ser una propiedad computada reactiva
    }
  },
  computed: {
    stages() {
      const storeStages = this.$store.getters.getStages
      return Array.isArray(storeStages) ? storeStages : []
    },
    sku() {
      if (!this.product) return ''
      return this.product.library_product__sku || (this.product.library_product && this.product.library_product.sku) || '---'
    },
    workOrderId() {
      if (!this.product) return ''
      return this.product.work_order__id || this.product.work_order || this.$attrs['work-order-id'] || '---'
    }
  },
  async mounted() {
    if (this.stages.length === 0) {
      try {
        await this.$store.dispatch('getStages')
      } catch (e) {
        console.error("Error al obtener etapas en DetailPrestages:", e)
      }
    }
  },
  methods: {
    findStage(stageInput) {
      if (!stageInput) return null
      // Si stageInput es un objeto (por la serialización depth=2 de la OT), extraemos su ID
      const stageId = typeof stageInput === 'object' ? stageInput.id : stageInput
      return this.stages.find(stage => stage.id == stageId)
    },
    getStageName(id) {
      const stage = this.findStage(id)
      return stage ? stage.name.toUpperCase() : 'PRODUCCIÓN'
    },
    getStageColor(id) {
      const stage = this.findStage(id)
      return stage ? stage.color : 'success'
    },
    getStageHex(id) {
      const stage = this.findStage(id)
      return stage ? 'var(--v-' + stage.color + '-base)' : 'var(--v-success-base)'
    },
    getStageIcon(id) {
      const stage = this.findStage(id)
      return stage ? 'fas fa-dot-circle' : 'fas fa-check-circle'
    }
  }
}
</script>

<style scoped>
.bg-grey-light { background-color: #f8f9fa; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.08); }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 2px; }
.shadow-sm { box-shadow: 0 4px 8px rgba(0,0,0,0.05) !important; }
.border-white { border: 2px solid white !important; }

.modern-timeline { position: relative; }
.timeline-item { position: relative; }
.timeline-dot-container { position: relative; height: 100%; }
.timeline-line { width: 2px; background: #eee; border-radius: 2px; position: absolute; top: 32px; bottom: 0; left: 15px; }
.timeline-content { background: #fbfbfc; padding: 16px 20px; border-radius: 12px; border: 1px solid rgba(0,0,0,0.03); transition: all 0.2s; }
.timeline-content:hover { transform: translateX(4px); background: #fff; border-color: rgba(0,0,0,0.06); box-shadow: 0 4px 12px rgba(0,0,0,0.03); }

.opacity-40 { opacity: 0.4; }
</style>